/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2021, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */
/* eslint-disable react/jsx-props-no-spreading */

import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { InlineLoading, Tabs, Tab, TabList, TabPanels, TabPanel } from '@carbon/react';
import ViewableJSONDisplay from './ViewableJSONDisplay';
import ExpandableDisplay from './ExpandableDisplay';
import CodeViewer from './CodeViewer';

function SchemaView({ schema, example, exampleLoading, exampleError, id, ...other }) {
  const { t } = useTranslation();

  const renderError = (error) => (
    <CodeViewer data-testid="channel-data-error" highlight={false}>
      {error}
    </CodeViewer>
  );

  const renderExample = (loading, example, error) => {
    let jsx;
    let copyableContent;

    if (loading) {
      jsx = <InlineLoading data-testid="example-loading-icon" />;
    } else if (example) {
      if (typeof example === 'object') {
        jsx = <ViewableJSONDisplay content={example} />;
        copyableContent = JSON.stringify(example);
      } else {
        jsx = <CodeViewer>{String(example)}</CodeViewer>;
        copyableContent = String(example);
      }
    } else if (error) {
      copyableContent = JSON.stringify(error);
      jsx = renderError(copyableContent);
    } else {
      copyableContent = t('no_examples');
      jsx = renderError(t('no_examples'));
    }

    return (
      <div data-testid="schema-example">
        <ExpandableDisplay copyableContent={copyableContent}>{jsx}</ExpandableDisplay>
      </div>
    );
  };

  return (
    <div {...other}>
      <Tabs className="channel__schema">
        <TabList aria-label="channel shema tabs">
          <Tab id={`${id}_schema`} title={t('schema')}>
            {t('schema')}
          </Tab>
          <Tab id={`${id}_example`} title={t('example')}>
            {t('example')}
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <div data-testid="schema">
              <ExpandableDisplay copyableContent={JSON.stringify(schema, null, 2)}>
                {schema && <ViewableJSONDisplay content={schema} />}
                {!schema && renderError(t('invalid_schema'))}
              </ExpandableDisplay>
            </div>
          </TabPanel>
          <TabPanel>{renderExample(exampleLoading, example, exampleError)}</TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  );
}

SchemaView.propTypes = {
  schema: PropTypes.object,
  example: PropTypes.any,
  exampleLoading: PropTypes.bool,
  exampleError: PropTypes.string
};

export default SchemaView;
